import React, { forwardRef, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { RootStyle } from '@ygtechnologies/rubus-ui';
import HeaderContext from '@/components/layout/Header/HeaderContext';
import { classnames } from '@/utils/html';
import theme from './FullscreenLayout.module.scss';

export interface FullscreenLayoutProps {
    /**
     * Render as a fixed, full-viewport overlay portalled to <body>. Use this when the view is
     * mounted inside chrome that its own router always renders (e.g. the buyer sidebar): the global
     * header is hidden either way, but only the overlay also covers a sibling sidebar. When false
     * (the default) the layout renders inline at its mount point — correct for top-level routes with
     * no surrounding chrome, such as the public showroom.
     */
    overlay?: boolean;
    /**
     * CSS-variable overrides applied via Rubus `RootStyle` for the duration of the layout (e.g.
     * `IMMERSIVE_STYLE_VARS`, which zeroes the app-shell spacing and supplies neutral brand defaults).
     */
    styleVars?: Record<string, string>;
    /** Extra class on the container element (mainly for inline mode). */
    className?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
}

// Shared chrome-less, full-screen layout for immersive views (the authenticated immersive
// configurator and the public showroom). Owns the three things every full-screen view needs:
//   1. hide the globally-mounted app `<Header />` via HeaderContext,
//   2. suppress the global `body { overflow-y: scroll }` so there's a single scroll region,
//   3. inject the immersive CSS variables via `RootStyle`,
// plus an optional body-portal overlay (see the `overlay` prop). The container ref is forwarded so
// callers can observe its size (e.g. the showroom's iframe-resize ResizeObserver).
export const FullscreenLayout = forwardRef<HTMLDivElement, FullscreenLayoutProps>(
    ({ overlay = false, styleVars, className, style, children }, ref) => {
        // Hide the global app Header so the view renders as a chromeless widget. Restored on unmount
        // so other routes still get the standard header.
        //
        // Depend on the stable `setHidden` setter, not the whole context object: the provider creates
        // a new value object on every state update, so depending on the context re-runs this effect on
        // every toggle — an infinite loop.
        const { setHidden } = useContext(HeaderContext);
        useEffect(() => {
            setHidden(true);
            return () => setHidden(false);
        }, [setHidden]);

        // Suppress the global `body { overflow-y: scroll }` rule (base.scss) while mounted. Full-screen
        // views size themselves to the viewport; without this the body adds a second scrollbar next to
        // any inner overflow region.
        //
        // For the `overlay` variant the app shell (sidebar, breadcrumb bar, sticky tabs) stays mounted
        // in normal flow *behind* the body-portal. Only suppressing `overflow-y` leaves `overflow-x:
        // auto` (CSS forces the other axis to `auto` when one is not `visible`), so wider-than-viewport
        // chrome underneath still shows a window scrollbar. Lock BOTH axes on <html> and <body> so the
        // covered shell can't scroll the page; the inline (non-overlay) showroom has no shell under it,
        // so suppressing body's y-scroll is enough.
        useEffect(() => {
            const html = document.documentElement;
            const body = document.body;
            const prev = {
                bodyOverflow: body.style.overflow,
                bodyOverflowY: body.style.overflowY,
                htmlOverflow: html.style.overflow,
            };
            if (overlay) {
                body.style.overflow = 'hidden';
                html.style.overflow = 'hidden';
            } else {
                body.style.overflowY = 'hidden';
            }
            return () => {
                body.style.overflow = prev.bodyOverflow;
                body.style.overflowY = prev.bodyOverflowY;
                html.style.overflow = prev.htmlOverflow;
            };
        }, [overlay]);

        const content = (
            <div ref={ref} className={classnames(overlay && theme.overlay, className)} style={style}>
                {styleVars && <RootStyle value={styleVars} />}
                {children}
            </div>
        );

        // Portal to <body> so the fixed overlay escapes any surrounding stacking context (e.g. the
        // buyer content area) and covers always-rendered sibling chrome. React context (router,
        // providers) still flows through the portal.
        return overlay ? createPortal(content, document.body) : content;
    }
);

FullscreenLayout.displayName = 'FullscreenLayout';
